import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// ✅ Fetch expenses
export const useExpenses = (token: string | null, options = { limit: 10000 }) => {
  return useQuery({
    queryKey: ['expenses', token],
    queryFn: async () => {
      if (!token) throw new Error('Authentication required');
      
      try {
        const response = await axios.get(`${API_URL}/expenses`, {
          headers: { Authorization: `Bearer ${token}` },
          params: options,
        });
        return response.data;
      } catch (error: any) {
        // ✅ Better error messages
        if (error.response?.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        if (error.response?.status === 403) {
          throw new Error('Access denied. Check your permissions.');
        }
        if (error.response?.status === 404) {
          throw new Error('Expenses not found.');
        }
        if (error.response?.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        if (!navigator.onLine) {
          throw new Error('No internet connection. Please check your network.');
        }
        throw new Error(error.response?.data?.error || 'Failed to load expenses');
      }
    },
    enabled: !!token,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
    retry: 3, // ✅ Retry 3 times
  });
};

// ✅ Create expense with optimistic update
export const useCreateExpense = (token: string | null) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (expenseData: any) => {
      if (!token) throw new Error('Authentication required');
      
      try {
        const response = await axios.post(`${API_URL}/expenses`, expenseData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        if (error.response?.status === 403) {
          throw new Error('Access denied.');
        }
        if (error.response?.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        if (!navigator.onLine) {
          throw new Error('No internet connection.');
        }
        throw new Error(error.response?.data?.error || 'Failed to create expense');
      }
    },
    onMutate: async (newExpense) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['expenses', token] });

      // Snapshot previous value
      const previousExpenses = queryClient.getQueryData(['expenses', token]);

      // Optimistically update
      queryClient.setQueryData(['expenses', token], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          expenses: [...(old.expenses || []), { ...newExpense, _id: 'temp-' + Date.now() }],
        };
      });

      return { previousExpenses };
    },
    onError: (_err, _newExpense, context) => {
      // Rollback on error
      if (context?.previousExpenses) {
        queryClient.setQueryData(['expenses', token], context.previousExpenses);
      }
    },
    onSettled: async () => {
      // ✅ Force refetch after mutation
      await queryClient.invalidateQueries({ 
        queryKey: ['expenses', token],
        refetchType: 'active', // Only refetch active queries
      });
    },
  });
};

// ✅ Update expense with optimistic update
export const useUpdateExpense = (token: string | null) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      if (!token) throw new Error('No token provided');
      const response = await axios.put(`${API_URL}/expenses/${id}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: ['expenses', token] });
      const previousExpenses = queryClient.getQueryData(['expenses', token]);

      queryClient.setQueryData(['expenses', token], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          expenses: old.expenses.map((exp: any) =>
            exp._id === id ? { ...exp, ...updates } : exp
          ),
        };
      });

      return { previousExpenses };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousExpenses) {
        queryClient.setQueryData(['expenses', token], context.previousExpenses);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ 
        queryKey: ['expenses', token],
        refetchType: 'active',
      });
    },
  });
};

// ✅ Delete expense
export const useDeleteExpense = (token: string | null) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error('No token provided');
      const response = await axios.delete(`${API_URL}/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['expenses', token] });
      const previousExpenses = queryClient.getQueryData(['expenses', token]);

      queryClient.setQueryData(['expenses', token], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          expenses: old.expenses.filter((exp: any) => exp._id !== id),
        };
      });

      return { previousExpenses };
    },
    onError: (_err, _id, context) => {
      if (context?.previousExpenses) {
        queryClient.setQueryData(['expenses', token], context.previousExpenses);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ 
        queryKey: ['expenses', token],
        refetchType: 'active',
      });
    },
  });
};

// ✅ Toggle paid
export const useTogglePaid = (token: string | null) => {
  return useUpdateExpense(token);
};

/**
 * @fileoverview Custom React Query hooks for expense management
 * @module hooks/useExpenses
 * 
 * This module provides hooks for:
 * - Fetching expenses with caching
 * - Creating expenses with optimistic updates
 * - Updating expenses with optimistic updates
 * - Deleting expenses with optimistic updates
 * 
 * All mutations include automatic rollback on error and cache invalidation on success.
 */

/**
 * Hook to fetch all expenses for the authenticated user
 * 
 * @param {string | null} token - JWT authentication token
 * @param {object} options - Query options
 * @param {number} options.limit - Maximum number of expenses to fetch (default: 10000)
 * 
 * @returns {UseQueryResult} React Query result object containing:
 * - data: { expenses: Expense[] } - Array of expense objects
 * - isLoading: boolean - Loading state
 * - error: Error | null - Error object if request failed
 * - refetch: Function - Manual refetch function
 * 
 * @example
 * const { data, isLoading, error } = useExpenses(token, { limit: 100 });
 * if (data) {
 *   console.log(data.expenses);
 * }
 * 
 * @features
 * - Automatic retry (3 attempts)
 * - Caching (stale after 2 minutes)
 * - Better error messages for common HTTP errors
 * - Only runs when token is present
 */

/**
 * Hook to create a new expense with optimistic updates
 * 
 * @param {string | null} token - JWT authentication token
 * 
 * @returns {UseMutationResult} React Query mutation object containing:
 * - mutate: Function - Execute the mutation
 * - isPending: boolean - Mutation in progress
 * - isSuccess: boolean - Mutation succeeded
 * - isError: boolean - Mutation failed
 * - data: Expense - Created expense data
 * 
 * @example
 * const createExpense = useCreateExpense(token);
 * 
 * createExpense.mutate({
 *   name: "Netflix",
 *   price: 15.99,
 *   dueDate: "2025-01-15",
 *   category: "Wave",
 *   wave: 1
 * }, {
 *   onSuccess: () => console.log("Created!"),
 *   onError: (error) => console.error(error)
 * });
 * 
 * @features
 * - Optimistic updates (UI updates before server confirms)
 * - Automatic rollback on error
 * - Cache invalidation on success
 * - Better error messages
 */

/**
 * Hook to update an existing expense with optimistic updates
 * 
 * @param {string | null} token - JWT authentication token
 * 
 * @returns {UseMutationResult} React Query mutation object
 * 
 * @example
 * const updateExpense = useUpdateExpense(token);
 * 
 * updateExpense.mutate({
 *   id: "expense-123",
 *   updates: { isPaid: true }
 * }, {
 *   onSuccess: () => console.log("Updated!"),
 *   onError: (error) => console.error(error)
 * });
 * 
 * @features
 * - Optimistic updates (instant UI feedback)
 * - Automatic rollback on error
 * - Cache invalidation on success
 */

/**
 * Hook to delete an expense with optimistic updates
 * 
 * @param {string | null} token - JWT authentication token
 * 
 * @returns {UseMutationResult} React Query mutation object
 * 
 * @example
 * const deleteExpense = useDeleteExpense(token);
 * 
 * deleteExpense.mutate("expense-123", {
 *   onSuccess: () => console.log("Deleted!"),
 *   onError: (error) => console.error(error)
 * });
 * 
 * @features
 * - Optimistic updates (expense removed from UI immediately)
 * - Automatic rollback on error
 * - Cache invalidation on success
 */